import { Select } from "antd";
import { Formik, Form, Field } from "formik";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import ButtonLoader from "@/Components/ButtonLoader";
import EditUserProfileAvatar from "@/Components/common/client/useraccess/EditUserProfileAvatar";
import FieldErrorMessage from "@/Components/common/FieldErrorMessage";
import ModalPopUp from "@/Components/common/ModalPopUp";
import { RequiredSymbol } from "@/Components/common/RequiredSymbol";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import { SelectDownIcon } from "@/Components/Icons/SelectDownIcon";
import {
  editClientUserData,
  toggleEditNewUserModal,
} from "@/reducers/client_user/clientUsers";
import { validateEmail } from "@/validations/auth/login";

const EditUserModal = ({ callback }) => {
  const { showEditNewUserModal, EditClientUserData } = useSelector(
    (state) => state.clientUsers,
  );
  const dispatch = useDispatch();
  const { roles } = useSelector((state) => state.userAccess);
  const [AddUserAvatar, setAddUserAvatar] = useState(null);
  const [disabledEditBtn, setDisabledEditBtn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarFile, setAvatarFile] = useState(null);

  useEffect(() => {
    setAddUserAvatar(EditClientUserData.profile_image);
    setAvatarFile("");
  }, [showEditNewUserModal]);

  useEffect(() => {
    return () => {
      dispatch(toggleEditNewUserModal(false));
    };
  }, []);

  const handleClose = () => {
    dispatch(toggleEditNewUserModal(false));
  };

  return (
    <ModalPopUp
      show={showEditNewUserModal}
      panelClass="max-w-[770px] max-sm:max-w-[400px]"
      onClose={() => {
        handleClose(true);
      }}
    >
      <div className="EditUserModal_wrapper flex flex-col gap-4">
        <div className="flex justify-between">
          <h3 className="text-[20px] font-semibold leading-[normal] text-black">
            Edit User
          </h3>
          <button
            onClick={handleClose}
            className="text-red outline-none ring-0"
          >
            <RejectIcon />
          </button>
        </div>
        <div>
          <Formik
            initialValues={{
              firstName:
                EditClientUserData?.first_name ||
                EditClientUserData?.client_user_name?.split(" ")[0] ||
                "",
              lastName:
                EditClientUserData?.last_name ||
                EditClientUserData?.client_user_name
                  ?.split(" ")
                  .slice(1)
                  .join(" ") ||
                "",
              profileImage: EditClientUserData?.profile_image,
              email:
                EditClientUserData?.email ||
                EditClientUserData?.user_email ||
                "",
              phoneNumber:
                EditClientUserData?.phone ||
                EditClientUserData?.phone_number ||
                "",
              directDial: EditClientUserData?.direct_dial || "",
              role: EditClientUserData?.role_id,
            }}
            enableReinitialize={true}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            validate={(values) => {
              const errors = {};
              if (!values.firstName) {
                errors.firstName = "First name is required";
              }
              if (!values.lastName) {
                errors.lastName = "Last name is required";
              }
              if (!values.email) {
                errors.email = "Email address is required";
              } else {
                const emailError = validateEmail(values.email);
                if (emailError) {
                  errors.email = emailError;
                }
              }
              if (!values.role) {
                errors.role = "Role Access is required";
              }
              if (values.role == "-") {
                errors.role =
                  "May be this user assigned role has been removed.  Please choose another role.";
              }
              return errors;
            }}
            onSubmit={(values, { setSubmitting }) => {
              dispatch(
                editClientUserData({
                  values,
                  isSubmitting,
                  setIsSubmitting,
                  callback,
                  avatarFile,
                  EditClientUserData,
                }),
              );
            }}
          >
            {({
              submitForm,

              errors,
              touched,
              setFieldValue,
              values,
            }) => (
              <Form>
                <div className="Add_User_Modal flex flex-col gap-6">
                  <EditUserProfileAvatar
                    AddUserAvatar={AddUserAvatar}
                    setAddUserAvatar={setAddUserAvatar}
                    avatarFile={avatarFile}
                    setAvatarFile={setAvatarFile}
                  />

                  <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
                    <div className="grid gap-3">
                      <label
                        htmlFor="firstName"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        First name <RequiredSymbol />
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="text"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 placeholder:text-[14px] focus:border-primary ${errors.firstName && touched.firstName ? "!border-red-500" : ""}`}
                          autoComplete="off"
                          name="firstName"
                          placeholder="Enter First name"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                        />
                        <FieldErrorMessage name="firstName" />
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <label
                        htmlFor="lastName"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Last name <RequiredSymbol />
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="text"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 placeholder:text-[14px] focus:border-primary ${errors.lastName && touched.lastName ? "!border-red-500" : ""}`}
                          autoComplete="off"
                          name="lastName"
                          placeholder="Enter Last name"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                        />
                        <FieldErrorMessage name="lastName" />
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <label
                        htmlFor="email"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Email Address <RequiredSymbol />
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="email"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 focus:border-primary ${errors.email && touched.email ? "!border-red-500" : ""}`}
                          autoComplete="off"
                          disabled={true}
                          name="email"
                          placeholder="Enter Email Address"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                        />
                        <FieldErrorMessage name="email" />
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <label
                        htmlFor="phoneNumber"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Phone Number
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="number"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 focus:border-primary ${errors.phoneNumber && touched.phoneNumber ? "!border-red-500" : ""}`}
                          autoComplete="off"
                          name="phoneNumber"
                          placeholder="Enter Phone Number"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                        />
                        <FieldErrorMessage name="phoneNumber" />
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <label
                        htmlFor="directDial"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Direct Dial
                      </label>
                      <div className="flex flex-col">
                        <Field
                          type="text"
                          className={`h-[40px] w-full rounded-lg border border-lightGrey px-4 !outline-none !ring-0 placeholder:text-[14px] focus:border-primary`}
                          autoComplete="off"
                          name="directDial"
                          placeholder="Enter Direct Dial"
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                        />
                        <FieldErrorMessage name="directDial" />
                      </div>
                    </div>
                    <div className="grid gap-3">
                      <label
                        htmlFor="role"
                        className="text-[14px] font-medium leading-[normal] text-txtColor"
                      >
                        Role Access <RequiredSymbol />
                      </label>
                      <div className="flex flex-col">
                        <Select
                          showSearch
                          style={{ width: "100%" }}
                          placeholder="Select Role Access"
                          optionFilterProp="children"
                          className={`ease min-h-[40px] w-full overflow-hidden rounded-lg border border-lightGrey text-[14px] font-medium leading-[normal] text-black transition-all duration-200 focus:border-primary ${errors.role && touched.role ? "!border-red-500" : ""}`}
                          name="role"
                          suffixIcon={<SelectDownIcon cls={"text-txtColor"} />}
                          onKeyUp={(e) => {
                            if (e.keyCode === 13) {
                              submitForm();
                            }
                          }}
                          onChange={(value) => {
                            setFieldValue("role", value);
                          }}
                          value={values.role}
                          disabled={
                            EditClientUserData.is_primary == 1 ? true : false
                          }
                        >
                          {roles.map((item, index) => {
                            const { role_name, id } = item;
                            return (
                              <Select.Option value={id} key={index}>
                                {role_name}
                              </Select.Option>
                            );
                          })}
                        </Select>
                        <FieldErrorMessage name="role" />
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end gap-[20px]">
                    <button
                      type="button"
                      className="transition-linear relative h-[45px] w-[120px] rounded-lg border border-primary bg-primary text-[14px] font-medium text-white hover:bg-none hover:text-primary"
                      onClick={submitForm}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <ButtonLoader /> : <span>Update</span>}
                    </button>
                    <button
                      type="button"
                      className="transition-linear h-[45px] w-[120px] rounded-lg border border-primary bg-none text-[14px] font-medium text-primary hover:bg-primary hover:text-white"
                      onClick={() => {
                        handleClose();
                        setFieldValue("name", "");
                        setFieldValue("email", "");
                        setFieldValue("phoneNumber", "");
                        setFieldValue("role", "");
                        setFieldValue("profile_image", "");
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </ModalPopUp>
  );
};

export default EditUserModal;
